import React from 'react';
import {
  AlertOutlined,
  ApartmentOutlined,
  DollarOutlined,
  GroupOutlined,
  UserAddOutlined,
} from '@ant-design/icons';

import SavingsTypesContainer from '@totem/components/administration/optimize/savingsTypes/savingsTypesContainer';
import SavingsTypesLayout, {
  SavingsTypesExtra,
} from '@totem/components/administration/optimize/savingsTypes/savingsTypesLayout';
import RulesContainer from '@totem/components/rulesEngine/report/rulesContainer';
import RulesLayout, {
  RulesExtra,
} from '@totem/components/rulesEngine/report/rulesLayout';
import EventConfigurationContainer from '@totem/components/services/threats/configuration/EventConfigurationContainer';
import EventConfigurationLayout, {
  EventConfigurationExtra,
} from '@totem/components/services/threats/configuration/EventConfigurationLayout';
import TicketAssignmentsContainer from '@totem/components/ticketing/assignments/TicketAssignmentsContainer';
import TicketAssignmentsLayout, {
  TicketAssignmentsExtra,
} from '@totem/components/ticketing/assignments/TicketAssignmentsLayout';
import TicketCategorizationContainer from '@totem/components/ticketing/categorizationManagement/TicketCategorizationContainer';
import TicketCategorizationLayout, {
  TicketCategorizationExtra,
} from '@totem/components/ticketing/categorizationManagement/TicketCategorizationLayout';
import TemplateManagementListPanel from '@totem/components/ticketing/templateManagement/list/TemplateListPanel';
import IBCard from '@totem/components/ui_components/antDesignOverrides/IBCard';
import { IBMenuItem } from '@totem/components/ui_components/menus/types';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import { UserProfile } from '@totem/types/user';

export const getMenuAdminConfiguration = (userProfile: UserProfile) => {
  const items: IBMenuItem[] = [
    {
      key: 'eventConfiguration',
      title: 'Event Configuration',
      label: 'Event Configuration',
      description: 'Manage event categories and ticketing configuration',
      route: '/dashboard/admin/eventConfiguration',
      content: (
        <EventConfigurationContainer>
          <IBCard
            isCovered
            title="Event Configuration"
            extra={<EventConfigurationExtra />}
          >
            <EventConfigurationLayout />
          </IBCard>
        </EventConfigurationContainer>
      ),
      icon: <AlertOutlined />,
      hasPermissions: () => {
        return permissions.internal.hasPermissions(userProfile);
      },
    },
    {
      key: 'rules',
      title: 'Rules',
      label: 'Rules',
      description: 'Internal Processing Rules',
      route: '/dashboard/admin/rules',
      content: (
        <RulesContainer>
          <IBCard isCovered title="Rules Engine" extra={<RulesExtra />}>
            <RulesLayout />
          </IBCard>
        </RulesContainer>
      ),
      icon: <GroupOutlined />,
      hasPermissions: () => {
        return permissions.internalOrganizationalAdministrator.hasPermissions(
          userProfile,
        );
      },
    },
    {
      key: 'adminServices',
      title: 'Services',
      label: 'Services',
      description: 'Service Definitions',
      route: '/dashboard/admin/services',
      icon: <GroupOutlined />,
      hasPermissions: () => {
        return permissions.internalSystemAdministrator.hasPermissions(
          userProfile,
        );
      },
    },
    {
      key: 'optimizeSavingsTypes',
      title: 'Optimize Savings Types',
      label: 'Optimize Savings Types',
      description: 'Savings Type options on Optimize tickets',
      route: '/dashboard/admin/optimizeSavingsTypes',
      content: (
        <SavingsTypesContainer>
          <IBCard
            isCovered
            title="Optimize Savings Types"
            extra={<SavingsTypesExtra />}
          >
            <SavingsTypesLayout />
          </IBCard>
        </SavingsTypesContainer>
      ),
      icon: <DollarOutlined />,
      hasPermissions: () => {
        return permissions.internalSystemAdministrator.hasPermissions(
          userProfile,
        );
      },
    },
    {
      key: 'ticketAssignments',
      title: 'Ticket Assignments',
      label: 'Ticket Assignments',
      description: 'Assign people to new tickets by Type / Category / Summary',
      route: '/dashboard/ticketAssignments',
      content: (
        <TicketAssignmentsContainer>
          <IBCard
            isCovered
            title="Ticket Assignments"
            extra={<TicketAssignmentsExtra />}
          >
            <TicketAssignmentsLayout />
          </IBCard>
        </TicketAssignmentsContainer>
      ),
      icon: <UserAddOutlined />,
      hasPermissions: () => {
        return (
          permissions.internalIBOrg.hasPermissions(userProfile) &&
          permissions.ticketingAdministrator.hasPermissions(userProfile)
        );
      },
    },
    {
      key: 'ticketingCategorization',
      title: 'Ticket Categorization',
      label: 'Ticket Categorization',
      description: 'Ticket Type / Category / Sub-Category Management',
      route: '/dashboard/ticketingCategorization',
      content: (
        <TicketCategorizationContainer>
          <IBCard
            isCovered
            title="Ticket Categorization"
            extra={<TicketCategorizationExtra />}
          >
            <TicketCategorizationLayout />
          </IBCard>
        </TicketCategorizationContainer>
      ),
      icon: <ApartmentOutlined />,
      hasPermissions: () => {
        return (
          permissions.internalIBOrg.hasPermissions(userProfile) &&
          permissions.ticketingAdministrator.hasPermissions(userProfile)
        );
      },
    },
    {
      key: 'ticketingTemplates',
      title: 'Ticketing Templates',
      label: 'Ticket Templates',
      description: 'Ticketing Task Template Management',
      route: '/dashboard/ticketingTemplates',
      content: <TemplateManagementListPanel />,
      icon: <GroupOutlined />,
      hasPermissions: () => {
        return (
          permissions.internalIBOrg.hasPermissions(userProfile) &&
          permissions.ticketingAdministrator.hasPermissions(userProfile)
        );
      },
    },
  ];

  return items.filter((chk) => chk.hasPermissions(userProfile));
};
